import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Paperclip, Play } from 'lucide-react';
import Dialog from '../ui/Dialog';
import Ltr from '../ui/Ltr';
import { useNearViewport } from '../../hooks/useNearViewport';
import { attachmentKind, audioBlobType } from './mediaModel';

/**
 * P-59a (D-235 §1) - an attachment in a conversation, shown instead of only a
 * download link: an image as a thumbnail (click = enlarge), a video in a
 * built-in `<video controls>` player, anything else as the download chip.
 *
 * The bytes come through `load()` - the SAME authenticated endpoint (and the
 * same permission check) as the download button; the blob never leaves this
 * browser tab, there is no public link. The download button stays.
 */
export interface AttachmentMediaProps {
  name: string;
  mime: string | null | undefined;
  size?: string | null;
  /** Fetch the file (authenticated). Called for an image thumbnail, or when the user opens a video. */
  load: () => Promise<Blob>;
  /** Save the file (the existing download). */
  onDownload: () => void;
}

function useBlobUrl(load: () => Promise<Blob>, enabled: boolean): { url: string; loading: boolean; error: boolean } {
  const [state, setState] = useState({ url: '', loading: enabled, error: false });
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let objectUrl = '';
    setState({ url: '', loading: true, error: false });
    loadRef.current()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, loading: false, error: false });
      })
      .catch(() => { if (!cancelled) setState({ url: '', loading: false, error: true }); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [enabled]);
  return state;
}

const chip = 'inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border border-surface-200 bg-surface-0 px-2 py-0.5 text-xs text-surface-700';

function NameChip({ name, size, onDownload }: { name: string; size?: string | null; onDownload: () => void }) {
  const { t } = useTranslation();
  return (
    <span className={chip}>
      <Paperclip size={12} className="flex-shrink-0" aria-hidden="true" />
      <bdi className="truncate">{name}</bdi>
      {size && <Ltr className="flex-shrink-0 text-surface-500">{size}</Ltr>}
      <button type="button" className="btn-icon text-text-2 hover:text-text" onClick={onDownload} aria-label={`${t('media.download')}: ${name}`} title={t('media.download')}>
        <Download size={14} aria-hidden="true" />
      </button>
    </span>
  );
}

function ImageAttachment({ name, size, load, onDownload }: Omit<AttachmentMediaProps, 'mime'>) {
  const { t } = useTranslation();
  // FIX-B38 (QX-801): fetch the image only when it nears the viewport (a long thread does not load every image at once).
  const holderRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(holderRef);
  const { url, loading, error } = useBlobUrl(load, near);
  const [open, setOpen] = useState(false);
  return (
    <div ref={holderRef} className="inline-flex max-w-full flex-col gap-1">
      {(loading || !near) && <div className="h-24 w-32 animate-pulse rounded-md bg-surface-100" aria-hidden="true" />}
      {error && <span className="text-xs text-danger-700" role="alert">{t('media.loadError')}</span>}
      {url && (
        <button type="button" onClick={() => setOpen(true)} className="block" aria-label={`${t('media.enlarge')}: ${name}`}>
          <img src={url} alt={name} loading="lazy" decoding="async" className="max-h-40 max-w-64 cursor-zoom-in rounded-md border border-surface-200 object-cover" />
        </button>
      )}
      <NameChip name={name} size={size} onDownload={onDownload} />
      {open && url && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          title={<bdi className="block truncate">{name}</bdi>}
          size="xl"
          className="max-w-5xl"
          footer={(
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>{t('media.close')}</button>
              <button type="button" className="btn btn-primary" onClick={onDownload}>
                <Download size={16} aria-hidden="true" />
                {t('media.download')}
              </button>
            </>
          )}
        >
          <div className="flex min-h-48 items-center justify-center rounded-lg bg-surface-subtle p-2">
            <img src={url} alt={name} className="max-h-[70vh] max-w-full rounded-lg object-contain" />
          </div>
        </Dialog>
      )}
    </div>
  );
}

function VideoAttachment({ name, mime, size, load, onDownload }: AttachmentMediaProps) {
  const { t } = useTranslation();
  const [requested, setRequested] = useState(false);
  // A screen recording is stored as .webm, which the file server labels audio/webm: give the player the real video type.
  const videoLoad = () => load().then((b) => (mime && !b.type.startsWith('video/') ? new Blob([b], { type: mime }) : b));
  const { url, loading, error } = useBlobUrl(videoLoad, requested);
  return (
    <div className="inline-flex max-w-full flex-col gap-1">
      {!requested && (
        <button
          type="button"
          onClick={() => setRequested(true)}
          className="inline-flex h-24 w-40 items-center justify-center gap-1.5 rounded-md border border-surface-200 bg-surface-50 text-sm text-primary-700 hover:bg-surface-100"
          aria-label={`${t('media.loadVideo')}: ${name}`}
        >
          <Play size={18} aria-hidden="true" />
          {t('media.loadVideo')}
        </button>
      )}
      {requested && loading && <div className="h-24 w-40 animate-pulse rounded-md bg-surface-100" aria-hidden="true" />}
      {requested && error && <span className="text-xs text-danger-700" role="alert">{t('media.loadError')}</span>}
      {url && (
        // A recording someone made: there is no caption file.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={url} controls playsInline preload="metadata" className="max-h-72 max-w-full rounded-md border border-surface-200 bg-black" />
      )}
      <NameChip name={name} size={size} onDownload={onDownload} />
    </div>
  );
}

/**
 * FIX-B38 (QX-800): a voice recording is an audio player (+ the download chip),
 * even when the server labelled its WebM video/webm. Fetched when it nears the viewport.
 */
function VoiceAttachment({ name, mime, size, load, onDownload }: AttachmentMediaProps) {
  const { t } = useTranslation();
  const holderRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(holderRef);
  const audioLoad = () => load().then((b) => {
    const type = audioBlobType(b.type, mime, name);
    return b.type.split(';')[0].trim().toLowerCase() === type ? b : new Blob([b], { type });
  });
  const { url, loading, error } = useBlobUrl(audioLoad, near);
  return (
    <div ref={holderRef} className="inline-flex min-w-0 max-w-full flex-col gap-1">
      {(loading || !near) && <div className="h-9 w-40 animate-pulse rounded-md bg-surface-100" aria-hidden="true" />}
      {error && <span className="text-xs text-danger-700" role="alert">{t('media.loadError')}</span>}
      {url && (
        // DQ-088: the native player is ~300px wide; inside a chat bubble at 390px it pushed the page sideways.
        // A voice message someone recorded: no caption file exists for user audio.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={url} controls preload="metadata" className="h-9 w-72 min-w-0 max-w-full" />
      )}
      <NameChip name={name} size={size} onDownload={onDownload} />
    </div>
  );
}

/** Image / video get an inline viewer; everything else renders `fallback` (the existing download chip). */
export default function AttachmentMedia({ mime, name, fallback, ...rest }: AttachmentMediaProps & { fallback: ReactNode }) {
  const kind = attachmentKind(mime, name);
  if (kind === 'image') return <ImageAttachment name={name} {...rest} />;
  if (kind === 'video') return <VideoAttachment name={name} mime={mime} {...rest} />;
  if (kind === 'audio') return <VoiceAttachment name={name} mime={mime} {...rest} />;
  return <>{fallback}</>;
}
